import type { OptimizerAuditV2Record } from '../../types/audit.types';
import type { WeightDiffDetailRow } from '../../types/snapshot.types';
import { formatAuditAccountType } from '../presentation/accountTypeLabels';
import type { ReportSubAccountDisplayMetadata } from '../snapshot/buildReportDisplayMetadata';

export const TOP_INSIGHT_MOVE_CAP = 3;

const uniqueLabels = (values: string[]): string[] => [
  ...new Set(values.filter((value) => value && value !== '—')),
];

const resolveMoveAccountLabels = (
  row: WeightDiffDetailRow,
  direction: 'buy' | 'sell',
  subAccountsById: Map<string, ReportSubAccountDisplayMetadata>,
): string[] => {
  const matching = row.subAccountRows.filter((subAccount) =>
    direction === 'buy' ? subAccount.deltaCents > 0 : subAccount.deltaCents < 0,
  );
  const source = matching.length > 0 ? matching : row.subAccountRows;

  return uniqueLabels(
    source.map((subAccount) => {
      const sleeveName = subAccountsById.get(subAccount.subAccountId)?.name;
      if (sleeveName) {
        return sleeveName;
      }

      return formatAuditAccountType(subAccount.accountType);
    }),
  );
};

/** AI context uses integer cents — org-service prompt bans dollar echo in output. */
const formatTopInsightMoveForAi = (
  row: WeightDiffDetailRow,
  direction: 'buy' | 'sell',
  subAccountsById: Map<string, ReportSubAccountDisplayMetadata>,
): string => {
  const ticker = row.label.trim();
  const amountCents = Math.abs(row.deltaCents);
  const accounts = resolveMoveAccountLabels(row, direction, subAccountsById);

  return [ticker, String(amountCents), accounts.join(', ')]
    .filter(Boolean)
    .join(' · ');
};

export const buildTopInsightMovesForAi = (
  diffRows: WeightDiffDetailRow[],
  subAccountsById: Map<string, ReportSubAccountDisplayMetadata> = new Map(),
): { largestBuys: string[]; largestSells: string[] } => {
  const largestBuys = [...diffRows]
    .filter((row) => row.deltaCents > 0)
    .sort((left, right) => right.deltaCents - left.deltaCents)
    .slice(0, TOP_INSIGHT_MOVE_CAP)
    .map((row) => formatTopInsightMoveForAi(row, 'buy', subAccountsById));

  const largestSells = [...diffRows]
    .filter((row) => row.deltaCents < 0)
    .sort((left, right) => left.deltaCents - right.deltaCents)
    .slice(0, TOP_INSIGHT_MOVE_CAP)
    .map((row) => formatTopInsightMoveForAi(row, 'sell', subAccountsById));

  return { largestBuys, largestSells };
};

export const collectInsightAccountTypes = (
  audit?: OptimizerAuditV2Record,
): string[] =>
  uniqueLabels(
    (audit?.subAccountResults ?? []).map((result) =>
      formatAuditAccountType(result.accountType),
    ),
  );
